"use client";

/**
 * ClaimCreditsButton
 * A self-contained floating action button that lets any user claim their credits
 * on a piece of media (track, release, video, artwork/marketplace item).
 *
 * - Visitor (not logged in) → redirects to /signin with return URL
 * - Logged-in user → opens a minimal claim dialog inline
 * - Uses the existing claimCredit() service + MediaCreditsPanel for the full UI
 */

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Award, Loader2, ShieldCheck, LogIn, Check, Play, Pause } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { claimCredit } from "@/services/media-credits";
import { useContributorRoles } from "@/hooks/use-app-config";
import { cn } from "@/lib/utils";

type MediaType = "work" | "release" | "video" | "marketplace";

interface ReleaseTrack {
  workId: string;
  title: string;
  trackNumber?: number;
  /** Audio source so the inline play button can preview the track without
   *  leaving the claim dialog. Either field is fine. */
  audioUrl?: string;
  mp3Url?: string;
  /** Optional cover for the player track entry. */
  imageUrl?: string;
  /** Optional artist label for the player. */
  artist?: string;
}

interface ClaimCreditsButtonProps {
  mediaId: string;
  mediaType: MediaType;
  mediaTitle: string;
  /** If true, renders as a compact icon+label button (default: false = full-size) */
  compact?: boolean;
  /** Icon-only button — even tighter than `compact`. Used in the media-player track header. */
  iconOnly?: boolean;
  className?: string;
  /** For `mediaType: 'release'` — list of tracks so the user can pick which ones
   *  they actually played on. One claim is submitted per selected track. */
  tracks?: ReleaseTrack[];
}

const FALLBACK_ROLES = [
  "Composer", "Lyricist", "Producer", "Performer", "Arranger",
  "Mixer", "Mastering Engineer", "Featuring Artist", "Photographer",
  "Graphic Designer", "Videographer", "Director", "Editor",
];

export default function ClaimCreditsButton({
  mediaId,
  mediaType,
  mediaTitle,
  compact = false,
  iconOnly = false,
  className,
  tracks = [],
}: ClaimCreditsButtonProps) {
  const { user, userProfile } = useAuth();
  const router = useRouter();
  const { toast } = useToast();
  const { items: roleOptions } = useContributorRoles();
  const { playTrack, currentTrack, isPlaying, togglePlay } = useMediaPlayer();

  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("");
  const [split, setSplit] = useState("");
  const [notes, setNotes] = useState("");
  const [claiming, setClaiming] = useState(false);
  // Default: every track pre-selected, since most claimants worked on the whole release.
  const [selectedTrackIds, setSelectedTrackIds] = useState<Set<string>>(
    () => new Set(tracks.map(t => t.workId).filter(Boolean))
  );

  const isReleaseWithTracks = mediaType === "release" && tracks.length > 0;

  const roles = roleOptions?.length ? roleOptions.map((r: any) => r.label ?? r) : FALLBACK_ROLES;

  function handleClick() {
    if (!user) {
      // Visitor → send to sign-in with redirect back
      const returnPath = typeof window !== "undefined" ? window.location.pathname : "";
      router.push(`/signin?redirect=${encodeURIComponent(returnPath)}`);
      return;
    }
    setOpen(true);
  }

  async function handleSubmit() {
    if (!user || !role) return;
    if (isReleaseWithTracks && selectedTrackIds.size === 0) {
      toast({ variant: "destructive", title: "Pick at least one track", description: "Tick the tracks you actually worked on." });
      return;
    }
    setClaiming(true);
    try {
      const baseClaim = {
        userId: user.uid,
        userName: userProfile?.displayName || user.displayName || "Unknown",
        userAvatar: userProfile?.profileImageUrl || user.photoURL || "",
        role,
        splitPercent: split ? parseFloat(split) : undefined,
        notes,
        status: "pending" as const,
        claimedBy: user.uid,
        claimedByName: userProfile?.displayName || user.displayName || "Unknown",
      };

      if (isReleaseWithTracks) {
        // Fan out: one credit-claim per selected TRACK (mediaType=work). This
        // is what the user has been asking for — claim on a release should let
        // you pick *which* tracks you actually played on.
        const targets = tracks.filter(t => t.workId && selectedTrackIds.has(t.workId));
        let ok = 0; let fail = 0;
        for (const t of targets) {
          try {
            await claimCredit({
              ...baseClaim,
              mediaId: t.workId,
              mediaType: "work",
              mediaTitle: t.title,
            });
            ok++;
          } catch (e) { console.error("claim failed", t.workId, e); fail++; }
        }
        toast({
          title: ok > 0 ? `${ok} claim${ok === 1 ? "" : "s"} submitted ✓` : "All claims failed",
          description: fail > 0
            ? `${ok} submitted, ${fail} failed. Admins will review the rest.`
            : "Admins will review and approve each one.",
          variant: ok === 0 ? "destructive" : "default",
        });
      } else {
        await claimCredit({
          ...baseClaim,
          mediaId,
          mediaType: mediaType === "marketplace" ? "work" : mediaType,
          mediaTitle,
        });
        toast({
          title: "Credit claim submitted ✓",
          description: "An admin will review your request. You'll be notified when it's approved.",
        });
      }
      setOpen(false);
      setRole(""); setSplit(""); setNotes("");
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Claim failed",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setClaiming(false);
    }
  }

  const isVisitor = !user;

  return (
    <>
      {iconOnly ? (
        <Button
          size="icon"
          variant="ghost"
          onClick={handleClick}
          title="Claim credits on this track"
          className={cn("h-9 w-9 text-muted-foreground hover:text-primary", className)}
        >
          <Award className="h-5 w-5" />
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={handleClick}
          className={cn("gap-1.5", className)}
        >
          {isVisitor
            ? <><LogIn className="h-3.5 w-3.5" />Claim Credits</>
            : <><Award className="h-3.5 w-3.5" />Claim Credits</>
          }
        </Button>
      )}

      {/* Claim dialog — only shown to logged-in users */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-primary" />
              Claim Your Credit
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Claiming a credit on <strong className="text-foreground">{mediaTitle}</strong>.
              An admin will verify and approve your claim before it appears publicly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Claimant info (read-only) */}
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg text-sm">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                {(userProfile?.displayName || user?.displayName || "?").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="font-medium">{userProfile?.displayName || user?.displayName}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <Badge className="ml-auto text-[9px] bg-emerald-600 text-white">Logged in</Badge>
            </div>

            {/* Track picklist — releases only. Tick the tracks you actually played on. */}
            {isReleaseWithTracks && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs">Tracks you worked on <span className="text-destructive">*</span></Label>
                  <button
                    type="button"
                    className="text-[10px] text-primary hover:underline"
                    onClick={() => {
                      if (selectedTrackIds.size === tracks.length) setSelectedTrackIds(new Set());
                      else setSelectedTrackIds(new Set(tracks.map(t => t.workId).filter(Boolean)));
                    }}
                  >
                    {selectedTrackIds.size === tracks.length ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-border bg-muted/20 divide-y divide-border/60">
                  {tracks.map((t) => {
                    const checked = selectedTrackIds.has(t.workId);
                    const audioSrc = t.audioUrl || t.mp3Url || "";
                    const isCurrent = currentTrack?.id === t.workId;
                    const isPlayingThis = isCurrent && isPlaying;
                    const canPlay = Boolean(audioSrc);
                    const handlePlay = (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!canPlay) return;
                      if (isCurrent) {
                        togglePlay();
                      } else {
                        playTrack({
                          id: t.workId,
                          title: t.title,
                          artist: t.artist || "",
                          imageUrl: t.imageUrl || "",
                          audioUrl: audioSrc,
                          duration: 0,
                        } as any);
                      }
                    };
                    return (
                      <div
                        key={t.workId}
                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/40"
                      >
                        {/* Play / pause preview — listen before you claim. */}
                        <button
                          type="button"
                          onClick={handlePlay}
                          disabled={!canPlay}
                          title={canPlay ? (isPlayingThis ? "Pause" : "Play preview") : "No preview available"}
                          className={cn(
                            "h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
                            canPlay
                              ? "bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground"
                              : "bg-muted text-muted-foreground/40 cursor-not-allowed"
                          )}
                          aria-label={isPlayingThis ? `Pause ${t.title}` : `Play ${t.title}`}
                        >
                          {isPlayingThis
                            ? <Pause className="h-3.5 w-3.5" />
                            : <Play className="h-3.5 w-3.5 ml-0.5" />}
                        </button>
                        <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setSelectedTrackIds(prev => {
                                const next = new Set(prev);
                                if (v) next.add(t.workId); else next.delete(t.workId);
                                return next;
                              });
                            }}
                          />
                          {t.trackNumber != null && (
                            <span className="text-[10px] text-muted-foreground font-mono w-5 text-right">{t.trackNumber}</span>
                          )}
                          <span className="text-xs truncate flex-1">{t.title}</span>
                          {checked && <Check className="h-3 w-3 text-emerald-500 flex-shrink-0" />}
                        </label>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Your role + split below will be applied to each ticked track ({selectedTrackIds.size} of {tracks.length}).
                </p>
              </div>
            )}

            {/* Role selector */}
            <div>
              <Label className="text-xs mb-1.5 block">{isReleaseWithTracks ? "Your role on the selected tracks" : `Your role on this ${mediaType === "video" ? "video" : "track"}`} <span className="text-destructive">*</span></Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select your role…" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r: string) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Split (optional) */}
            <div>
              <Label className="text-xs mb-1.5 block">
                Ownership split % <span className="text-muted-foreground">(optional — leave blank if unsure)</span>
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={split}
                onChange={e => setSplit(e.target.value)}
                placeholder="e.g. 50"
                className="h-9 text-sm"
              />
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs mb-1.5 block">Supporting notes <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="E.g. I wrote the chorus, IPI number 12345678901, contract ref…"
                rows={3}
                className="text-sm resize-none"
              />
            </div>

            {/* Info box */}
            <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
              <span>
                Your claim is submitted for admin review. You can also verify your identity via BankID, Freja eID, or passport in <strong className="text-foreground">Publishing → Claim Account</strong> for faster approval.
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={claiming}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={claiming || !role}
              className="bg-primary text-primary-foreground"
            >
              {claiming
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</>
                : <><Award className="h-4 w-4 mr-2" />Submit Claim</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
